import { useEffect, useMemo, useReducer } from 'react';

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import {
  Cluster,
  ClusterOffer,
  Clustering,
  ClusteringRequest,
  LatLongTuple,
  ManualCluster,
  OfferRecommendation,
} from '~/components/LERE/types';

import LereService from '~/services/LereService';

import { ENABLE_CLUSTERING_EDITING } from '../constants';

import { buildClusterOverlays } from './clusterMapGeo';
import { ClusterMapState, NoisePolicy, clusterMapReducer, initialClusterMapState } from './clusterMapState';
import {
  buildManualClusterLabels,
  clampOpacity,
  filterClustersByIdOrManualCluster,
  getClusterColor,
  getManualClusterIds,
  getManualClusterValidation,
  getNoiseSummary,
  getPolygonBounds,
  getVisibleClusters,
  parseParamsJson,
  pickParamsJson,
  toManualClusters,
} from './clusterMapUtils';

const DEFAULT_CENTER: LatLongTuple = [20, 100];
const DEFAULT_ZOOM = 3;
const CLUSTERINGS_STALE_TIME = 60_000;
const CLUSTERS_STALE_TIME = 30_000;
const EMPTY_CLUSTERINGS: Array<Clustering> = [];
const EMPTY_CLUSTERS: Array<Cluster> = [];
const EMPTY_CLUSTER_OFFERS: Array<ClusterOffer> = [];
const EMPTY_RECOMMENDATIONS: Array<OfferRecommendation> = [];
const CLUSTER_OFFERS_STALE_TIME = 60_000;
const RECOMMENDATIONS_STALE_TIME = 60_000;

function isNoisePolicy(value?: string): value is NoisePolicy {
  return value === 'nearest_cluster' || value === 'leave_noise';
}

export interface UseClusterMapOptions {
  onLereError: (error: unknown) => void;
}

export function useClusterMap({ onLereError }: UseClusterMapOptions) {
  const [state, dispatch] = useReducer(clusterMapReducer, initialClusterMapState);
  const {
    selectedClusterId,
    selectedRunId,
    previewData,
    isEditing,
    cutKm,
    minClusterSize,
    minSamples,
    maxAssignDistKm,
    noisePolicy,
    opacity,
    maxClusters,
    minClusterOffers,
    showBorderBreakingClusters,
    manualClustersDraft,
    manualFocus,
    isPickingManualBounds,
    manualPickPoints,
    newManualCluster,
    filterClusterId,
    filterManualClusterName,
    offerIdSearch,
    submittedOfferId,
  } = state;

  const setState = (patch: Partial<ClusterMapState>) => {
    dispatch({ type: 'MERGE_STATE', payload: patch });
  };

  const updateNewManualCluster = (patch: Partial<ManualCluster>) => {
    dispatch({ type: 'UPDATE_NEW_MANUAL_CLUSTER', payload: patch });
  };

  const setManualPickPoints = (points: Array<LatLongTuple>) => {
    dispatch({ type: 'SET_MANUAL_PICK_POINTS', payload: points });
    updateNewManualCluster({ polygon: points });
  };

  const resetNewManualCluster = () => {
    dispatch({ type: 'RESET_NEW_MANUAL_CLUSTER' });
  };

  const clusteringsQuery = useQuery({
    queryKey: ['lere', 'clusterings'],
    queryFn: () => LereService.getClusterings(),
    placeholderData: keepPreviousData,
    staleTime: CLUSTERINGS_STALE_TIME,
    select: (data) => data.clusterings,
  });

  const clustersQuery = useQuery({
    queryKey: ['lere', 'clusters', selectedRunId || 'latest'],
    queryFn: () => LereService.getClusters(selectedRunId ? { run_id: selectedRunId } : undefined),
    placeholderData: keepPreviousData,
    staleTime: CLUSTERS_STALE_TIME,
    select: (data) => data.clusters,
  });

  // Fetch offer-to-cluster assignments (only after user clicks "Look up")
  const clusterOffersQuery = useQuery({
    queryKey: ['lere', 'clusterOffers', selectedRunId || 'latest'],
    queryFn: () => LereService.getClusterOffers(selectedRunId ? { run_id: selectedRunId } : undefined),
    placeholderData: keepPreviousData,
    staleTime: CLUSTER_OFFERS_STALE_TIME,
    select: (data) => data.offers,
    enabled: submittedOfferId.length > 0,
  });

  // Fetch offer-to-offer recommendations (only after user clicks "Look up")
  const offerRecommendationsQuery = useQuery({
    queryKey: ['lere', 'offerRecommendations', submittedOfferId],
    queryFn: () => LereService.getOfferRecommendations({ offer_id: submittedOfferId }),
    staleTime: RECOMMENDATIONS_STALE_TIME,
    select: (data) => data.recommendations,
    enabled: submittedOfferId.length > 0,
  });

  const clusterings = clusteringsQuery.data ?? EMPTY_CLUSTERINGS;
  const clusters = clustersQuery.data ?? EMPTY_CLUSTERS;
  const clusterOffers = clusterOffersQuery.data ?? EMPTY_CLUSTER_OFFERS;
  const offerRecommendations = offerRecommendationsQuery.data ?? EMPTY_RECOMMENDATIONS;

  useEffect(() => {
    if (clusterings.length && !selectedRunId) {
      dispatch({ type: 'MERGE_STATE', payload: { selectedRunId: clusterings[0].run_id } });
    }
  }, [clusterings, selectedRunId]);

  const selectedRun = clusterings.length
    ? clusterings.find((run) => run.run_id === selectedRunId) ?? clusterings[0]
    : undefined;

  const baseParams = parseParamsJson(selectedRun?.params_json);

  const activeParamsJson = pickParamsJson(previewData?.params_json, selectedRun?.params_json);

  const runParams = parseParamsJson(activeParamsJson);
  const runParamsDisplay =
    runParams && Object.keys(runParams).length > 0
      ? runParams
      : activeParamsJson
      ? { raw_params_json: activeParamsJson }
      : null;

  useEffect(() => {
    if (!ENABLE_CLUSTERING_EDITING || isEditing) {
      return;
    }
    if (baseParams) {
      dispatch({
        type: 'RESET_PARAMS',
        payload: {
          cutKm: typeof baseParams.cut_km === 'number' ? baseParams.cut_km : initialClusterMapState.cutKm,
          minClusterSize:
            typeof baseParams.min_cluster_size === 'number'
              ? baseParams.min_cluster_size
              : initialClusterMapState.minClusterSize,
          minSamples:
            typeof baseParams.min_samples === 'number' ? baseParams.min_samples : initialClusterMapState.minSamples,
          maxAssignDistKm:
            typeof baseParams.max_assign_dist_km === 'number'
              ? baseParams.max_assign_dist_km
              : initialClusterMapState.maxAssignDistKm,
          noisePolicy: isNoisePolicy(selectedRun?.noise_policy)
            ? selectedRun.noise_policy
            : initialClusterMapState.noisePolicy,
          manualClustersDraft: toManualClusters(baseParams),
        },
      });
      return;
    }

    dispatch({
      type: 'RESET_PARAMS',
      payload: {
        cutKm: initialClusterMapState.cutKm,
        minClusterSize: initialClusterMapState.minClusterSize,
        minSamples: initialClusterMapState.minSamples,
        maxAssignDistKm: initialClusterMapState.maxAssignDistKm,
        noisePolicy: initialClusterMapState.noisePolicy,
        manualClustersDraft: [],
      },
    });
  }, [baseParams, isEditing, selectedRun?.noise_policy]);

  const previewMutation = useMutation({
    mutationKey: ['lere', 'clusters', 'run'],
    mutationFn: (body: ClusteringRequest) => {
      if (!ENABLE_CLUSTERING_EDITING) {
        throw new Error('Clustering editing is disabled');
      }
      // This will fail gracefully if POST endpoint doesn't exist
      return LereService.runClustering(body);
    },
    onSuccess: (response) => {
      const nextState: Partial<ClusterMapState> = {
        previewData: response,
        selectedClusterId: null,
      };
      if (isNoisePolicy(response.noise_policy)) {
        nextState.noisePolicy = response.noise_policy;
      }
      dispatch({ type: 'MERGE_STATE', payload: nextState });
    },
    onError: (error) => onLereError(error),
  });

  const activeClusters = previewData?.clusters ?? clusters;
  const hasActiveClusterData = Boolean(previewData || clustersQuery.data);
  const activeClustersCount = previewData?.clusters?.length ?? clusters.length;

  const noiseSummary = getNoiseSummary(previewData);

  const combinedError = clusteringsQuery.error ?? clustersQuery.error ?? previewMutation.error;

  useEffect(() => {
    if (combinedError) {
      onLereError(combinedError);
    }
  }, [combinedError, onLereError]);

  const runChange = (runId: string) => {
    setState({
      selectedRunId: runId,
      selectedClusterId: null,
      previewData: null,
      isEditing: false,
      manualFocus: null,
      manualPickPoints: [],
      isPickingManualBounds: false,
    });
  };

  const selectedCluster = activeClusters.find((cluster) => cluster.cluster_id === selectedClusterId);

  // Manual clusters sourced from the run params (always available, regardless of editing mode)
  const runManualClusters = toManualClusters(baseParams);

  // When editing, prefer the draft; otherwise use the run's manual clusters
  const effectiveManualClusters = isEditing && manualClustersDraft.length > 0 ? manualClustersDraft : runManualClusters;

  // Apply cluster ID / manual cluster name filters first, then apply visibility filters
  const filteredByIdOrManual = filterClustersByIdOrManualCluster(
    activeClusters,
    { filterClusterId, filterManualClusterName },
    effectiveManualClusters,
  );
  const visibleClusters = getVisibleClusters(filteredByIdOrManual, minClusterOffers, maxClusters);

  useEffect(() => {
    if (selectedClusterId !== null && !visibleClusters.some((cluster) => cluster.cluster_id === selectedClusterId)) {
      dispatch({ type: 'MERGE_STATE', payload: { selectedClusterId: null } });
    }
  }, [selectedClusterId, visibleClusters]);

  const mapCenter = manualFocus
    ? manualFocus.center
    : selectedCluster
    ? ([selectedCluster.centroid_latitude, selectedCluster.centroid_longitude] as LatLongTuple)
    : visibleClusters.length
    ? ([visibleClusters[0].centroid_latitude, visibleClusters[0].centroid_longitude] as LatLongTuple)
    : DEFAULT_CENTER;

  const mapZoom = manualFocus ? manualFocus.zoom : selectedCluster ? 6 : DEFAULT_ZOOM;

  const manualClusterLabels = buildManualClusterLabels(visibleClusters, effectiveManualClusters);

  const manualClusterIds = getManualClusterIds(activeClusters, effectiveManualClusters);

  const clusterOverlays = buildClusterOverlays(visibleClusters, manualClusterLabels, showBorderBreakingClusters);

  const selectedClusterColor = selectedCluster ? getClusterColor(selectedCluster.cluster_id) : undefined;

  const isLoading = clusteringsQuery.isLoading || clustersQuery.isLoading || previewMutation.isPending;
  const isFetching = clusteringsQuery.isFetching || clustersQuery.isFetching;
  const isPreviewPending = previewMutation.isPending;

  const preview = () => {
    if (!ENABLE_CLUSTERING_EDITING) {
      return;
    }
    const manualClustersPayload = manualClustersDraft
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
      .filter((cluster) => cluster.enabled !== false && cluster.polygon.length >= 3)
      .map((cluster) => ({
        name: cluster.name,
        polygon: cluster.polygon,
        protected: cluster.protected,
      }));
    const requestBody: ClusteringRequest = {
      params: {
        cut_km: cutKm,
        min_cluster_size: minClusterSize,
        min_samples: minSamples,
        max_assign_dist_km: maxAssignDistKm,
        manual_clusters: manualClustersPayload.length ? manualClustersPayload : undefined,
      },
      noise_policy: noisePolicy,
    };

    previewMutation.mutate(requestBody);
  };

  const enterEdit = () => {
    if (!ENABLE_CLUSTERING_EDITING) {
      return;
    }
    setState({ isEditing: true });
  };

  const exitEdit = () => {
    setState({
      isEditing: false,
      previewData: null,
      selectedClusterId: null,
      manualFocus: null,
      manualPickPoints: [],
      isPickingManualBounds: false,
    });
    resetNewManualCluster();
  };

  const selectCluster = (clusterId: number | null) => {
    setState({
      selectedClusterId: clusterId,
      manualFocus: null,
    });
  };

  const goToManualCluster = (cluster: ManualCluster) => {
    const bounds = getPolygonBounds(cluster.polygon);
    if (!bounds) {
      return;
    }
    const center: LatLongTuple = [(bounds.latMin + bounds.latMax) / 2, (bounds.lonMin + bounds.lonMax) / 2];
    setState({
      manualFocus: { center, zoom: 6 },
      selectedClusterId: null,
    });
  };

  const manualPickComplete = (points: Array<LatLongTuple>) => {
    updateNewManualCluster({
      polygon: points,
    });
  };

  const clearPickPoints = () => {
    setManualPickPoints([]);
    updateNewManualCluster({ polygon: [] });
  };

  const togglePickingBounds = () => {
    setState({ isPickingManualBounds: !isPickingManualBounds });
  };

  const toggleManualClusterProtected = (index: number) => {
    dispatch({ type: 'TOGGLE_MANUAL_CLUSTER_PROTECTED', payload: index });
  };

  const toggleManualClusterEnabled = (index: number) => {
    dispatch({ type: 'TOGGLE_MANUAL_CLUSTER_ENABLED', payload: index });
  };

  const manualPickPolyline = manualPickPoints.length >= 2 && manualPickPoints.length < 4 ? manualPickPoints : null;
  const manualPickPolygon = manualPickPoints.length === 4 ? [...manualPickPoints, manualPickPoints[0]] : null;

  const fillOpacity = clampOpacity(opacity);

  const manualClusterValidation = getManualClusterValidation(newManualCluster);
  const canAddManualCluster =
    Boolean(newManualCluster.name.trim()) &&
    manualClusterValidation.hasPolygon &&
    manualClusterValidation.polygonInRange;

  const addManualCluster = () => {
    if (!canAddManualCluster) {
      return;
    }

    const manualCluster: ManualCluster = {
      name: newManualCluster.name.trim(),
      polygon: newManualCluster.polygon,
      enabled: newManualCluster.enabled ?? true,
      protected: newManualCluster.protected,
    };

    dispatch({ type: 'ADD_MANUAL_CLUSTER', payload: manualCluster });
    resetNewManualCluster();
    setManualPickPoints([]);
    setState({ isPickingManualBounds: false });
  };

  const errorMessage = combinedError
    ? combinedError instanceof Error
      ? combinedError.message
      : String(combinedError)
    : null;

  // Extract unique manual cluster names for the filter dropdown
  const manualClusterNames = effectiveManualClusters
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    .filter((mc) => mc.enabled !== false)
    .map((mc) => mc.name)
    .filter((name) => name.trim().length > 0);

  const clusterIdRange =
    activeClusters.length > 0
      ? {
          min: Math.min(...activeClusters.map((c) => c.cluster_id)),
          max: Math.max(...activeClusters.map((c) => c.cluster_id)),
        }
      : null;

  // Offer ID lookup: find which cluster an offer belongs to (based on submitted ID)
  const offerLookupResult = useMemo(() => {
    if (!submittedOfferId) {
      return null;
    }
    if (clusterOffersQuery.isLoading) {
      return { status: 'loading' as const };
    }
    const match = clusterOffers.find((co) => co.offer_id === submittedOfferId);
    if (match) {
      return { status: 'found' as const, clusterId: match.cluster_id };
    }
    return { status: 'not_found' as const };
  }, [submittedOfferId, clusterOffersQuery.isLoading, clusterOffers]);

  // Map each recommendation to its cluster (if cluster offers data is available)
  const offerRecommendationsWithClusters = useMemo(() => {
    if (!offerRecommendations.length || !clusterOffers.length) {
      return offerRecommendations.map((rec) => ({ ...rec, clusterId: null as number | null }));
    }
    const offerToCluster = new Map(clusterOffers.map((co) => [co.offer_id, co.cluster_id]));
    return offerRecommendations.map((rec) => ({
      ...rec,
      clusterId: offerToCluster.get(rec.offer_id) ?? null,
    }));
  }, [offerRecommendations, clusterOffers]);

  const isOfferRecommendationsLoading = offerRecommendationsQuery.isLoading;

  const derived = {
    clusterings,
    selectedRun,
    runParamsDisplay,
    activeClustersCount,
    hasActiveClusterData,
    visibleClusters,
    noiseSummary,
    mapCenter,
    mapZoom,
    clusterOverlays,
    selectedCluster,
    selectedClusterColor,
    manualPickPolyline,
    manualPickPolygon,
    fillOpacity,
    manualClusterValidation,
    manualClusterIds,
    canAddManualCluster,
    errorMessage,
    manualClusterNames,
    clusterIdRange,
    offerLookupResult,
    offerRecommendationsWithClusters,
    isOfferRecommendationsLoading,
  };

  const ui = {
    isClusteringsLoading: clusteringsQuery.isLoading,
    isLoading,
    isFetching,
    isPreviewPending,
  };

  const submitOfferSearch = () => {
    const trimmed = offerIdSearch.trim();
    if (trimmed) {
      setState({ submittedOfferId: trimmed });
    }
  };

  const clearOfferSearch = () => {
    setState({ offerIdSearch: '', submittedOfferId: '' });
  };

  const goToOfferCluster = () => {
    if (offerLookupResult?.status === 'found') {
      setState({
        filterClusterId: String(offerLookupResult.clusterId),
        filterManualClusterName: '',
        selectedClusterId: offerLookupResult.clusterId,
      });
    }
  };

  const actions = {
    setState,
    setManualPickPoints,
    updateNewManualCluster,
    runChange,
    enterEdit,
    preview,
    exitEdit,
    selectCluster,
    goToManualCluster,
    goToOfferCluster,
    submitOfferSearch,
    clearOfferSearch,
    manualPickComplete,
    togglePickingBounds,
    clearPickPoints,
    toggleManualClusterEnabled,
    toggleManualClusterProtected,
    addManualCluster,
  };

  return { state, derived, ui, actions };
}
